/**
 * กิลด์ (data/guild.ts): found one for 5,000 ตำลึง, or join a simulated guild; invite, leave, kick, ranks, the notice,
 * donations to the coffer, and the guild level that contribution reaches (its passive buff through `regenMult`).
 *
 * The player's guild lives in `progress.guild` (declared below). In this single-player build every other member is
 * simulated: an arena contender (data/arena.ts) who answers an invitation at once. A server keeps the same state per
 * guild and runs these handlers for each member's commands (docs/ONLINE.md §5, "guild v1").
 */
import {
  FREE_RECRUITS,
  GUILD,
  GUILD_COLORS,
  GUILD_REGISTRAR_REACH,
  GUILD_REGISTRAR_TITLE,
  GUILD_REGISTRARS,
  guildLevelFor,
  guildRank,
  isGuildRank,
  rankOrder,
  SIM_GUILDS,
  simGuild,
  type GuildRankId,
  type GuildWarMode,
} from '../data/guild';
import { freeAt } from '../path';
import type { Entity, PlayerCommand } from '../world';
import type { SystemHost, WorldSystem } from './types';

export interface GuildMember {
  name: string;
  rank: GuildRankId;
  /** Strength in the war's duel model (data/guild.ts `rosterPower`). */
  power: number;
  contribution: number;
}

/** The player's guild, as the player sees it. */
export interface GuildState {
  /** A simulated guild's id, or `own` for one the player founded. */
  id: string;
  name: string;
  color: string;
  /** The player's rank. */
  rank: GuildRankId;
  /** Everyone else in the guild. */
  members: GuildMember[];
  /** ตำลึง in the coffer. */
  coffer: number;
  /** The guild's total contribution (its level comes from it). */
  contribution: number;
  /** The player's own contribution. */
  own: number;
  notice: string;
  /** Signed up for the next war, and against whom (systems/guildWar.ts). */
  warSignup?: GuildWarMode;
  /** Won the last war: the day's title, held until the next war starts. */
  warTitle?: boolean;
  /** กิลด์ศัตรู: guild ids this guild declared enemies, one-sided (systems/guildBanner.ts). */
  enemies?: string[];
  /** ป้ายสังหาร this guild has up (systems/guildBanner.ts). */
  banner?: GuildBannerState;
  /** Seconds before this guild may raise another banner. */
  bannerCool?: number;
}

/** A kill banner up: whom it hunts, the time left and the score. */
export interface GuildBannerState {
  target: string;
  targetName: string;
  /** Seconds of game time left. */
  left: number;
  /** Kills by this guild's side, and by the hunted side. */
  ours: number;
  theirs: number;
}

declare module '../progress' {
  interface PlayerProgress {
    /** The player's guild (systems/guild.ts); absent: in none. */
    guild?: GuildState;
  }
}

export type GuildCommand =
  | { t: 'guildFound'; name: string; color?: string }
  | { t: 'guildJoin'; id: string }
  | { t: 'guildInvite'; name?: string }
  | { t: 'guildLeave' }
  | { t: 'guildKick'; name: string }
  | { t: 'guildRank'; name: string; rank: GuildRankId }
  | { t: 'guildNotice'; text: string }
  | { t: 'guildDonate'; gold: number }
  /** The leader closes the guild for good. */
  | { t: 'guildDisband' }
  /** A line in the guild chat (the hud's กิลด์ channel). */
  | { t: 'guildChat'; text: string };

export type GuildEventKind = 'founded' | 'joined' | 'left' | 'invited' | 'kicked' | 'rank' | 'notice' | 'donated' | 'level' | 'registrar' | 'disbanded';
export type GuildEvent = { type: 'guild'; what: GuildEventKind; name?: string; level?: number };

/** Members including the player. */
export const guildSize = (g: GuildState): number => g.members.length + 1;
export const guildLevel = (g: GuildState) => guildLevelFor(g.contribution);

/** Adds contribution to the guild; the new level when it rose, else null. */
export function addGuildContribution(g: GuildState, n: number): number | null {
  const before = guildLevelFor(g.contribution).level;
  g.contribution += n;
  const after = guildLevelFor(g.contribution).level;
  return after > before ? after : null;
}

/** How many hold a rank, the player included. */
const holding = (g: GuildState, rank: GuildRankId) =>
  g.members.filter((m) => m.rank === rank).length + (g.rank === rank ? 1 : 0);

export class GuildSystem implements WorldSystem {
  readonly name = 'guild';

  private readonly registrars = new Set<number>();

  constructor(private readonly h: SystemHost) {}

  setup(): void {
    const w = this.h.world;
    for (const r of GUILD_REGISTRARS) {
      if (r.zone !== w.map.zone) continue;
      const base = r.at === 'plaza' ? w.map.plaza : w.map.landmarks?.[r.at];
      if (!base) continue;
      let at = { x: base.x + r.dx, y: base.y + r.dy };
      search: for (let k = 0; k <= 6; k++)
        for (let j = -k; j <= k; j++)
          for (let i = -k; i <= k; i++) {
            if (Math.max(Math.abs(i), Math.abs(j)) !== k) continue;
            const px = Math.floor(base.x + r.dx) + i + 0.5;
            const py = Math.floor(base.y + r.dy) + j + 0.5;
            if (freeAt(w.map, px, py)) {
              at = { x: px, y: py };
              break search;
            }
          }
      const e = this.h.add({ kind: 'npc', x: at.x, y: at.y, sprite: r.sprite, name: r.name, title: GUILD_REGISTRAR_TITLE, facing: r.facing ?? 1 });
      this.registrars.add(e.id);
    }
  }

  private isRegistrar(e: Entity): boolean {
    return e.kind === 'npc' && this.registrars.has(e.id);
  }

  /** Whether the player may found or join here: near a registrar, or the zone has none. */
  nearRegistrar(): boolean {
    if (this.registrars.size === 0) return true;
    const p = this.h.world.player;
    for (const e of this.h.world.entities.values())
      if (this.isRegistrar(e) && Math.hypot(e.x - p.x, e.y - p.y) <= GUILD_REGISTRAR_REACH) return true;
    return false;
  }

  talk(npc: Entity): boolean {
    if (!this.isRegistrar(npc)) return false;
    this.h.face(npc, this.h.world.player);
    this.h.emit({ type: 'guild', what: 'registrar' });
    return true;
  }

  refuse(cmd: PlayerCommand): boolean {
    if ((cmd.t === 'guildFound' || cmd.t === 'guildJoin') && !this.nearRegistrar()) {
      this.h.notice('ก่อตั้งหรือเข้ากิลด์ได้ที่นายทะเบียนกิลด์เท่านั้น');
      return true;
    }
    return false;
  }

  private get g(): GuildState | undefined {
    return this.h.world.progress.guild;
  }

  private changed(e: GuildEvent, text: string): void {
    this.h.emit(e);
    this.h.emit({ type: 'progressChanged' });
    this.h.notice(text);
  }

  private levelUp(level: number | null): void {
    if (level !== null) this.changed({ type: 'guild', what: 'level', level }, `กิลด์ขึ้นระดับ ${level}`);
  }

  /** The guild's passive buff: regeneration by guild level. */
  regenMult(): number {
    const g = this.g;
    return g ? guildLevel(g).regen : 1;
  }

  readonly commands = {
    guildFound: (c: { name: string; color?: string }) => {
      const pr = this.h.world.progress;
      if (pr.guild) return this.h.notice('ท่านอยู่ในกิลด์แล้ว');
      const name = String(c.name ?? '').trim();
      if (!name || name.length > GUILD.nameMax) return this.h.notice(`ชื่อกิลด์ต้องยาว 1–${GUILD.nameMax} ตัวอักษร`);
      if (SIM_GUILDS.some((s) => s.name === name)) return this.h.notice('มีกิลด์ชื่อนี้แล้ว');
      if (pr.gold < GUILD.foundCost) return this.h.notice(`ตำลึงไม่พอ (ต้องใช้ ${GUILD.foundCost})`);
      pr.gold -= GUILD.foundCost;
      const color = (GUILD_COLORS as readonly string[]).includes(c.color ?? '') ? c.color! : GUILD_COLORS[0];
      pr.guild = { id: 'own', name, color, rank: 'leader', members: [], coffer: 0, contribution: 0, own: 0, notice: '' };
      this.changed({ type: 'guild', what: 'founded', name }, `ก่อตั้งกิลด์ ${name}`);
    },

    guildJoin: (c: { id: string }) => {
      const pr = this.h.world.progress;
      if (pr.guild) return this.h.notice('ท่านอยู่ในกิลด์แล้ว');
      const s = simGuild(c.id);
      if (!s) return this.h.notice('ไม่พบกิลด์นี้');
      if (s.members.length + 1 > guildLevelFor(s.contribution).cap) return this.h.notice('กิลด์นี้สมาชิกเต็มแล้ว');
      pr.guild = {
        id: s.id,
        name: s.name,
        color: s.color,
        rank: 'member',
        members: s.members.map((m) => ({ ...m, contribution: 0 })),
        coffer: 0,
        contribution: s.contribution,
        own: 0,
        notice: s.notice,
      };
      this.changed({ type: 'guild', what: 'joined', name: s.name }, `เข้าร่วมกิลด์ ${s.name}`);
    },

    guildInvite: (c: { name?: string }) => {
      const g = this.g;
      if (!g) return this.h.notice('ท่านยังไม่มีกิลด์');
      if (!guildRank(g.rank).canInvite) return this.h.notice('ตำแหน่งของท่านเชิญสมาชิกไม่ได้');
      if (guildSize(g) >= guildLevel(g).cap) return this.h.notice('กิลด์สมาชิกเต็มแล้ว');
      const taken = new Set(g.members.map((m) => m.name));
      const who = FREE_RECRUITS.find((r) => !taken.has(r.name) && (c.name === undefined || r.name === c.name));
      if (!who) return this.h.notice('ไม่มีผู้ตอบรับคำเชิญ');
      g.members.push({ name: who.name, rank: 'member', power: who.power, contribution: 0 });
      this.changed({ type: 'guild', what: 'invited', name: who.name }, `${who.name} เข้าร่วมกิลด์`);
    },

    guildLeave: () => {
      const pr = this.h.world.progress;
      const g = pr.guild;
      if (!g) return this.h.notice('ท่านยังไม่มีกิลด์');
      delete pr.guild;
      this.changed({ type: 'guild', what: 'left', name: g.name }, `ออกจากกิลด์ ${g.name}`);
    },

    guildKick: (c: { name: string }) => {
      const g = this.g;
      if (!g) return this.h.notice('ท่านยังไม่มีกิลด์');
      if (!guildRank(g.rank).canKick) return this.h.notice('ตำแหน่งของท่านขับสมาชิกไม่ได้');
      const i = g.members.findIndex((m) => m.name === c.name);
      if (i < 0) return this.h.notice('ไม่พบสมาชิกผู้นี้');
      if (rankOrder(g.members[i]!.rank) <= rankOrder(g.rank)) return this.h.notice('ขับผู้มีตำแหน่งเท่าหรือสูงกว่าไม่ได้');
      g.members.splice(i, 1);
      this.changed({ type: 'guild', what: 'kicked', name: c.name }, `ขับ ${c.name} ออกจากกิลด์`);
    },

    guildRank: (c: { name: string; rank: GuildRankId }) => {
      const g = this.g;
      if (!g) return this.h.notice('ท่านยังไม่มีกิลด์');
      if (!guildRank(g.rank).canPromote) return this.h.notice('ตำแหน่งของท่านแต่งตั้งไม่ได้');
      const m = g.members.find((x) => x.name === c.name);
      if (!m || !isGuildRank(c.rank)) return this.h.notice('ไม่พบสมาชิกผู้นี้');
      if (c.rank === 'leader') {
        // Handing the guild over: the old leader becomes a deputy.
        m.rank = 'leader';
        g.rank = 'deputy';
      } else {
        if (m.rank !== c.rank && holding(g, c.rank) >= guildRank(c.rank).max)
          return this.h.notice(`ตำแหน่ง${guildRank(c.rank).name}เต็มแล้ว`);
        m.rank = c.rank;
      }
      this.changed({ type: 'guild', what: 'rank', name: m.name }, `${m.name} เป็น${guildRank(m.rank).name}`);
    },

    guildNotice: (c: { text: string }) => {
      const g = this.g;
      if (!g) return this.h.notice('ท่านยังไม่มีกิลด์');
      if (!guildRank(g.rank).canNotice) return this.h.notice('ตำแหน่งของท่านแก้ประกาศกิลด์ไม่ได้');
      g.notice = String(c.text ?? '').slice(0, GUILD.noticeMax);
      this.changed({ type: 'guild', what: 'notice' }, 'แก้ประกาศกิลด์แล้ว');
    },

    guildDonate: (c: { gold: number }) => {
      const pr = this.h.world.progress;
      const g = pr.guild;
      if (!g) return this.h.notice('ท่านยังไม่มีกิลด์');
      const gold = Math.floor(Number(c.gold));
      if (!(gold >= GUILD.donateMin)) return this.h.notice(`บริจาคอย่างน้อย ${GUILD.donateMin} ตำลึง`);
      if (pr.gold < gold) return this.h.notice('ตำลึงไม่พอ');
      pr.gold -= gold;
      g.coffer += gold;
      const n = gold * GUILD.contributionPerGold;
      g.own += n;
      const up = addGuildContribution(g, n);
      this.changed({ type: 'guild', what: 'donated', name: g.name }, `บริจาค ${gold} ตำลึงเข้าคลังกิลด์`);
      this.levelUp(up);
    },
    guildDisband: () => {
      const pr = this.h.world.progress;
      const g = pr.guild;
      if (!g) return this.h.notice('ท่านยังไม่มีกิลด์');
      if (g.rank !== 'leader') return this.h.notice('มีแต่เจ้ากิลด์ที่ยุบกิลด์ได้');
      delete pr.guild;
      this.changed({ type: 'guild', what: 'disbanded', name: g.name }, `ยุบกิลด์ ${g.name} แล้ว`);
    },
    guildChat: (c: { text: string }) => {
      if (!this.g) return this.h.notice('ท่านยังไม่มีกิลด์');
      const text = String(c.text ?? '').trim().slice(0, GUILD.chatMax);
      if (text) this.h.log('guild', `${this.h.world.progress.name}: ${text}`);
    },
  };
}
